<template>
  <div v-if="show" class="drilldown-overlay" @click.self="$emit('close')">
    <div class="drilldown-container">
      <div class="drilldown-header">
        <div class="header-content">
          <h2>{{ categoryName }}</h2>
          <div class="header-subtitle">
            {{ monthName }} {{ year }} ({{ transactions.length }}) - Net:
            {{ formatNetAmount(totalDebit, totalCredit) }}
          </div>
        </div>
        <div class="header-buttons">
          <button
            class="sort-button"
            :class="{ active: sortByAmount }"
            @click="sortByAmount = !sortByAmount"
            :title="sortByAmount ? 'Sorted by amount' : 'Sort by amount'"
          >
            <i class="fas fa-sort-amount-down"></i>
          </button>
          <button
            class="refresh-button"
            :disabled="refreshing"
            @click="$emit('refresh')"
            title="Refresh data"
          >
            <i
              class="fas"
              :class="refreshing ? 'fa-spinner fa-spin' : 'fa-sync-alt'"
            ></i>
          </button>
          <button class="close-button" @click="$emit('close')">×</button>
        </div>
      </div>

      <!-- Column Headers -->
      <div class="column-headers">
        <span class="header-date">Date</span>
        <div class="header-amounts">
          <span class="header-debit">Debit</span>
          <span class="header-credit">Credit</span>
        </div>
      </div>

      <!-- Transaction List -->
      <div class="transactions-container">
        <div
          v-for="transaction in sortedTransactions"
          :key="transaction.Id"
          class="transaction-card"
          @dblclick="openTransactionInLedger(transaction.Id)"
        >
          <div class="transaction-header">
            <span class="transaction-date">{{
              formatDate(transaction.BankRefDatetime)
            }}</span>
            <div class="transaction-amounts">
              <span
                class="transaction-debit"
                :class="{ 'has-value': transaction.Debit }"
              >
                {{ formatSingleAmount(transaction.Debit) }}
              </span>
              <span
                class="transaction-credit"
                :class="{ 'has-value': transaction.Credit }"
              >
                {{ formatSingleAmount(transaction.Credit) }}
              </span>
            </div>
          </div>
          <div class="transaction-description">
            {{ transaction.Description || transaction.BankRefDesc }}
          </div>
          <div class="transaction-footer">
            <span class="transaction-account">
              <div>Account: {{ transaction.Account || 'Unassigned' }}</div>
              <div v-if="transaction.childAccounts" class="child-accounts">
                Split: {{ transaction.childAccounts }}
              </div>
            </span>
            <span
              class="transaction-status"
              :class="{ matched: transaction.matched }"
            >
              {{ transaction.matched ? 'Matched' : 'Unmatched' }}
            </span>
          </div>
        </div>

        <!-- Empty State -->
        <div v-if="transactions.length === 0" class="empty-state">
          <i class="fas fa-check-circle"></i>
          <p>No uncategorized transactions for this month</p>
        </div>
      </div>
    </div>

    <!-- Ledger iframe overlay -->
    <div
      v-if="iframeUrl"
      class="ledger-iframe-overlay"
      @click.self="iframeUrl = null"
    >
      <div class="ledger-iframe-container">
        <div class="ledger-iframe-title-bar">
          <span>{{ iframeTitle }}</span>
          <button class="ledger-iframe-close-btn" @click="iframeUrl = null">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="ledger-iframe-scroll-wrapper">
          <div v-if="iframeLoading" class="ledger-iframe-spinner-overlay">
            <i class="fas fa-spinner fa-spin"></i>
            <span>Loading…</span>
          </div>
          <iframe
            :src="iframeUrl"
            class="ledger-iframe"
            @load="iframeLoading = false"
          ></iframe>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TransactionsDrilldown',
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    categoryName: {
      type: String,
      default: 'Transactions',
    },
    transactions: {
      type: Array,
      default: () => [],
    },
    month: {
      type: Number,
      required: true,
    },
    year: {
      type: Number,
      required: true,
    },
    refreshing: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      sortByAmount: false,
      iframeUrl: null,
      iframeTitle: '',
      iframeLoading: false,
    };
  },
  computed: {
    sortedTransactions() {
      if (!this.sortByAmount) {
        return this.transactions;
      }
      return [...this.transactions].sort((a, b) => {
        // eslint-disable-next-line prettier/prettier
        const amountA = Math.abs(
          parseFloat(a.Debit) || parseFloat(a.Credit) || 0,
        );
        // eslint-disable-next-line prettier/prettier
        const amountB = Math.abs(
          parseFloat(b.Debit) || parseFloat(b.Credit) || 0,
        );
        return amountB - amountA; // Descending
      });
    },
    transactionCount() {
      return this.transactions.length;
    },
    monthName() {
      const months = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
      ];
      return months[this.month];
    },
    totalDebit() {
      return this.transactions.reduce((sum, transaction) => {
        const amount = parseFloat(transaction.Debit) || 0;
        return sum + amount;
      }, 0);
    },
    totalCredit() {
      return this.transactions.reduce((sum, transaction) => {
        const amount = parseFloat(transaction.Credit) || 0;
        return sum + amount;
      }, 0);
    },
  },
  methods: {
    closeDialog() {
      this.$emit('close');
    },

    formatDate(dateString) {
      const date = new Date(dateString);
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      const year = date.getFullYear().toString().slice(-2);
      return `${month}/${day}/${year}`;
    },

    formatSingleAmount(amount) {
      if (!amount || amount === 0) return '';
      const absAmount = Math.abs(parseFloat(amount));
      return absAmount.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    formatNetAmount(debit, credit) {
      const net = (parseFloat(credit) || 0) - (parseFloat(debit) || 0);
      const absNet = Math.abs(net);
      const formatted = absNet.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      if (net < 0) {
        return `($${formatted})`;
      }
      return `$${formatted}`;
    },

    formatAmount(debit, credit) {
      const amount = debit || credit || 0;
      const absAmount = Math.abs(amount);
      const formatted = absAmount.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

      // Debits (expenses) are shown in parentheses
      if (debit < 0) {
        return `($${formatted})`;
      }
      return `$${formatted}`;
    },

    openTransactionInLedger(transactionId) {
      // Build URL for MainPage with search parameter and YTD date range
      const searchString = `:${transactionId}`;

      // Calculate YTD date range
      const today = new Date();
      const currentYear = today.getFullYear();
      const currentMonth = String(today.getMonth() + 1).padStart(2, '0');
      const currentDay = String(today.getDate()).padStart(2, '0');
      const startDate = `${currentYear}-01-01`;
      const endDate = `${currentYear}-${currentMonth}-${currentDay}`;

      this.iframeTitle = `Transaction :${transactionId}`;
      this.iframeLoading = true;
      this.iframeUrl = `/main?page=bank&q=${encodeURIComponent(searchString)}&start=${startDate}&end=${endDate}&hideNav=1`;
    },
  },
};
</script>

<style scoped>
.drilldown-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.25);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 20001;
}

.drilldown-container {
  background-color: white;
  border-radius: 8px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.drilldown-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 20px;
  background: #a080a8;
  color: white;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
}

.header-content {
  flex: 1;
}

.drilldown-header h2 {
  margin: 0 0 8px 0;
  font-size: 1.3em;
  font-weight: 600;
}

.header-subtitle {
  font-size: 0.95em;
  opacity: 0.95;
  font-weight: 400;
}

.header-buttons {
  display: flex;
  align-items: center;
  gap: 8px;
}

.sort-button,
.refresh-button {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  font-size: 16px;
  cursor: pointer;
  padding: 6px 10px;
  border-radius: 4px;
  transition: all 0.2s;
}

.sort-button:hover,
.refresh-button:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.3);
}

.sort-button.active {
  background: rgba(255, 255, 255, 0.4);
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.5);
}

.refresh-button:disabled {
  opacity: 0.7;
  cursor: wait;
}

.close-button {
  background: none;
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  padding: 5px 10px;
  transition: opacity 0.2s;
  line-height: 1;
}

.close-button:hover {
  opacity: 0.8;
}

.column-headers {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 15px;
  background: #f5f5f5;
  border-bottom: 1px solid #e0e0e0;
  font-size: 0.85em;
  font-weight: 600;
  color: #666;
}

.header-date {
  min-width: 70px;
}

.header-amounts {
  display: flex;
  gap: 20px;
  margin-right: 17px;
}

.header-debit,
.header-credit {
  min-width: 80px;
  text-align: right;
}

.header-debit {
  color: #d32f2f;
}

.header-credit {
  color: #2e7d32;
}

.transactions-container {
  flex: 1;
  overflow-y: auto;
  padding: 15px;
  /* Hide scrollbar */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
}

.transactions-container::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}

.transaction-card {
  background: #f9f9f9;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 15px;
  margin-bottom: 5px;
  transition: all 0.2s ease;
  padding-bottom: 0px;
  padding-top: 3px;
}

.transaction-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-color: #a080a8;
}

.transaction-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.transaction-date {
  font-size: 0.9em;
  color: #666;
  font-weight: 500;
  min-width: 70px;
}

.transaction-amounts {
  display: flex;
  gap: 20px;
}

.transaction-debit,
.transaction-credit {
  font-size: 1em;
  font-weight: 600;
  font-family: 'Courier New', monospace;
  min-width: 80px;
  text-align: right;
  color: #999;
}

.transaction-debit.has-value {
  color: #d32f2f;
}

.transaction-credit.has-value {
  color: #2e7d32;
}

.transaction-amount {
  font-size: 1.1em;
  font-weight: 600;
  color: #d32f2f;
  font-family: 'Courier New', monospace;
}

.transaction-description {
  font-size: 0.95em;
  color: #333;
  margin-bottom: 8px;
  line-height: 1.4;
}

.transaction-footer {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  font-size: 0.85em;
  color: #777;
  margin-bottom: 10px;
}

.transaction-account {
  font-style: italic;
}

.child-accounts {
  margin-top: 4px;
  font-size: 0.9em;
  color: #a080a8;
  font-style: normal;
}

.transaction-status {
  padding: 2px 8px;
  border-radius: 3px;
  background: #ffa726;
  color: white;
  font-size: 0.9em;
}

.transaction-status.matched {
  background: #66bb6a;
}

.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #999;
}

.empty-state i {
  font-size: 48px;
  color: #66bb6a;
  margin-bottom: 16px;
}

.empty-state p {
  font-size: 1.1em;
  margin: 0;
}

/* Ledger iframe overlay */
.ledger-iframe-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.55);
  z-index: 30000;
  display: flex;
  justify-content: center;
  align-items: flex-start;
}

.ledger-iframe-container {
  width: 90vw;
  max-width: 1400px;
  height: 100vh;
  background: #fff;
  overflow: hidden;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.4);
  display: flex;
  flex-direction: column;
}

.ledger-iframe-title-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 14px;
  background: #333;
  color: #eee;
  font-size: 0.95em;
  flex-shrink: 0;
}

.ledger-iframe-close-btn {
  background: #555;
  border: 1px solid #777;
  color: #eee;
  border-radius: 50%;
  width: 26px;
  height: 26px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7em;
}

.ledger-iframe-close-btn:hover {
  background: #d32f2f;
}

.ledger-iframe-scroll-wrapper {
  flex: 1;
  overflow: hidden;
  position: relative;
}

.ledger-iframe-spinner-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #1e1e2e;
  z-index: 1;
  gap: 12px;
  color: #ccc;
  font-size: 1.1em;
}

.ledger-iframe-spinner-overlay .fa-spinner {
  font-size: 2em;
  color: #4fc3f7;
}

.ledger-iframe {
  border: none;
  width: 100%;
  height: 100%;
}
</style>
